import { computeZScores, mapZScoreToPercent, type MetricPolarity } from "@/lib/peer-group/engine";
import { cagrOverYears } from "@/lib/stats";
import type { Fundamentals, HistoricalSeries } from "@/lib/data/types";

/**
 * Fuenf-Achsen-Qualitaetsprofil fuer den Radar-Chart der Peer-Group-Engine (siehe
 * backlog.md -> "Institutionelle Peer-Group-Engine", Radar-Chart-Spec). Jede Achse
 * fasst 1-2 Rohkennzahlen zu einem 0-100-Wert zusammen (Z-Score-Mittelwert relativ zur
 * Peer-Gruppe, siehe mapZScoreToPercent).
 *
 * Abweichung von der Spec: "Zinsdeckung" (Interest Coverage) ist im Datenmodell nicht
 * verfuegbar (keine Zinsaufwand-Kennzahl in Fundamentals) - die Bilanz-Achse nutzt
 * stattdessen Net-Debt/EBITDA (bereits zentral in der Score-Engine verwendet) neben
 * Schuldenquote (D/E). "Buybacks" werden ueber den 3-Jahres-Rueckgang der ausstehenden
 * Aktienanzahl approximiert (historical.sharesOutstanding), da keine direkte
 * Rueckkauf-Kennzahl vorliegt.
 */

export const RADAR_AXES = [
  "Ertragskraft",
  "Wachstum",
  "Bilanz",
  "Kapitalallokation",
  "Aktionärsrendite",
] as const;

export type RadarAxis = (typeof RADAR_AXES)[number];

export interface RadarPeerInput {
  symbol: string;
  fundamentals: Fundamentals;
  historical: HistoricalSeries;
  marketCap?: number;
}

export interface RadarProfile {
  symbol: string;
  scores: Record<RadarAxis, number | undefined>;
}

interface DerivedMetrics {
  netMargin?: number;
  fcfYield?: number;
  revenueCagr3y?: number;
  epsCagr3y?: number;
  debtToEquity?: number;
  netDebtToEbitda?: number;
  roic?: number;
  roe?: number;
  dividendYield?: number;
  buybackRate?: number;
}

function deriveMetrics(peer: RadarPeerInput): DerivedMetrics {
  const { fundamentals: f, historical: h, marketCap } = peer;
  const netDebtToEbitda =
    f.netDebt !== undefined && f.ebitda !== undefined && f.ebitda !== 0
      ? f.netDebt / f.ebitda
      : undefined;
  const fcfYield =
    f.freeCashflow !== undefined && marketCap !== undefined && marketCap !== 0
      ? f.freeCashflow / marketCap
      : undefined;
  const sharesCagr3y = cagrOverYears(h.sharesOutstanding, 3);

  return {
    netMargin: f.netMargin,
    fcfYield,
    revenueCagr3y: cagrOverYears(h.revenue, 3),
    epsCagr3y: cagrOverYears(h.eps, 3),
    debtToEquity: f.debtToEquity,
    netDebtToEbitda,
    roic: f.roic,
    roe: f.roe,
    dividendYield: f.dividendYield,
    buybackRate: sharesCagr3y !== undefined ? -sharesCagr3y : undefined,
  };
}

const METRIC_POLARITY: Record<keyof DerivedMetrics, MetricPolarity> = {
  netMargin: "higher-is-better",
  fcfYield: "higher-is-better",
  revenueCagr3y: "higher-is-better",
  epsCagr3y: "higher-is-better",
  debtToEquity: "lower-is-better",
  netDebtToEbitda: "lower-is-better",
  roic: "higher-is-better",
  roe: "higher-is-better",
  dividendYield: "higher-is-better",
  buybackRate: "higher-is-better",
};

const AXIS_METRICS: Record<RadarAxis, (keyof DerivedMetrics)[]> = {
  Ertragskraft: ["netMargin", "fcfYield"],
  Wachstum: ["revenueCagr3y", "epsCagr3y"],
  Bilanz: ["debtToEquity", "netDebtToEbitda"],
  Kapitalallokation: ["roic", "roe"],
  "Aktionärsrendite": ["dividendYield", "buybackRate"],
};

/** Standardisiert alle 10 zugrunde liegenden Kennzahlen ueber die Peer-Gruppe -
 * gemeinsame Grundlage fuer den Radar-Chart (computeRadarProfiles) UND den
 * "Target-Fit"-Toggle (computeTargetFitScores in engine.ts), damit beide exakt
 * dieselben Z-Scores verwenden statt sie zweimal leicht unterschiedlich zu berechnen. */
export function computeMetricZScores(
  peers: RadarPeerInput[],
): Map<string, Record<string, number | undefined>> {
  const derived = peers.map((p) => ({ symbol: p.symbol, metrics: deriveMetrics(p) }));

  const zScoresByMetric = new Map<keyof DerivedMetrics, Map<string, number | undefined>>();
  for (const metricKey of Object.keys(METRIC_POLARITY) as (keyof DerivedMetrics)[]) {
    const inputs = derived.map((d) => ({ symbol: d.symbol, value: d.metrics[metricKey] }));
    const zScores = computeZScores(inputs, METRIC_POLARITY[metricKey]);
    zScoresByMetric.set(metricKey, new Map(zScores.map((z) => [z.symbol, z.zScore])));
  }

  const result = new Map<string, Record<string, number | undefined>>();
  for (const { symbol } of derived) {
    const metrics: Record<string, number | undefined> = {};
    for (const metricKey of Object.keys(METRIC_POLARITY) as (keyof DerivedMetrics)[]) {
      metrics[metricKey] = zScoresByMetric.get(metricKey)?.get(symbol);
    }
    result.set(symbol, metrics);
  }
  return result;
}

/** Berechnet fuer jede Aktie der Peer-Gruppe die 5 Radar-Achsen (0-100). Braucht
 * mindestens 2 Aktien, sonst gibt es nichts zu standardisieren (siehe computeZScores). */
export function computeRadarProfiles(peers: RadarPeerInput[]): RadarProfile[] {
  const zScoresBySymbol = computeMetricZScores(peers);

  return peers.map(({ symbol }) => {
    const metricZScores = zScoresBySymbol.get(symbol) ?? {};
    const scores = {} as Record<RadarAxis, number | undefined>;
    for (const axis of RADAR_AXES) {
      const zValues = AXIS_METRICS[axis]
        .map((key) => metricZScores[key])
        .filter((z): z is number => z !== undefined);
      scores[axis] =
        zValues.length > 0
          ? mapZScoreToPercent(zValues.reduce((sum, z) => sum + z, 0) / zValues.length)
          : undefined;
    }
    return { symbol, scores };
  });
}
